import React from 'react';
import { Anchor, Box, Button, Divider, Group, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { IconFingerprint } from '@tabler/icons-react';
import FlowAlert from '@/components/auth/flow/components/parts/Flow.Alert';
import emailInputClean, { EMAIL_MAX } from '@/function/auth/email.input.clean';
import emailValid from '@/function/auth/email.valid';
import flowMessageOf from '@/function/auth/flow.message.of';
import passkeyAvailable from '@/function/auth/passkey.available';
import { BEAUTH_PASSWORD_MAX } from '@/config/beauth.config';
import type { TypeAuthFlow } from '@/types/auth/types.auth.flow';

type Props = { flow: TypeAuthFlow; initialEmail: string; onForgot: (email: string) => void };

type Errors = { email?: string; password?: string };

const PASSWORD_STEP = 'password.verify';

const LoginStart: React.FC<Props> = ({ flow, initialEmail, onForgot }) => {
    const passkeySupported = React.useMemo(() => passkeyAvailable(), []);
    const [email, setEmail] = React.useState(flow.hint ?? initialEmail);
    const [password, setPassword] = React.useState('');
    const [errors, setErrors] = React.useState<Errors>({});
    const message = React.useMemo(() => (flow.failure ? flowMessageOf(flow.failure, flow.screen.id === 'password' ? 'password' : 'idle') : null), [flow.failure, flow.screen.id]);
    const emailError = errors.email ?? (message?.field === 'email' ? message.text : undefined);
    const passwordError = errors.password ?? (message?.field === 'password' ? message.text : undefined);
    const busyPassword = flow.busy && flow.choice !== 'passkey';

    React.useEffect(() => {
        if (message?.field === 'password') setPassword('');
    }, [message]);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (flow.busy) return;
        const address = email.trim();
        const next: Errors = {};
        if (!address) next.email = 'Serve l’email con cui accedi';
        else if (!emailValid(address)) next.email = 'Questo indirizzo non sembra un’email';
        if (!password) next.password = 'Serve la password';
        setErrors(next);
        if (next.email || next.password) return;

        const current = flow.state;
        const reusable = current.phase === 'step' && flow.hint === address && current.allowed.some((item) => item.step === PASSWORD_STEP);
        const state = reusable ? current : await flow.start(address, undefined, 'password');
        if (state.phase !== 'step' || !state.allowed.some((item) => item.step === PASSWORD_STEP)) return;
        await flow.submit(PASSWORD_STEP, { password });
    };

    return (
        <form method="post" onSubmit={(event) => void submit(event)} noValidate>
            <Stack gap="md">
                <FlowAlert message={message && message.field !== 'email' && message.field !== 'password' ? message : null} />
                <TextInput
                    withAsterisk
                    label="Email"
                    placeholder="nome@besuites.it"
                    type="email"
                    inputMode="email"
                    autoComplete="username"
                    maxLength={EMAIL_MAX}
                    value={email}
                    error={emailError}
                    onChange={(event) => { setErrors((current) => ({ ...current, email: undefined })); setEmail(emailInputClean(event.currentTarget.value)); }}
                />
                <Box>
                    <PasswordInput
                        withAsterisk
                        label="Password"
                        placeholder="La tua password"
                        autoComplete="current-password"
                        maxLength={BEAUTH_PASSWORD_MAX}
                        value={password}
                        error={passwordError}
                        onChange={(event) => { setErrors((current) => ({ ...current, password: undefined })); setPassword(event.currentTarget.value.slice(0, BEAUTH_PASSWORD_MAX)); }}
                    />
                    <Group justify="flex-start" gap={5} mt={5}>
                        <Text size="sm" c="dimmed">Password dimenticata?</Text>
                        <Anchor component="button" type="button" size="sm" style={{ minHeight: 44 }} onClick={() => onForgot(email.trim())}>Recuperala</Anchor>
                    </Group>
                </Box>

                <Button type="submit" mih={44} loading={busyPassword} disabled={flow.busy}>Entra</Button>

                {passkeySupported ? (
                    <>
                        <Divider label="oppure" labelPosition="center" />
                        <Button
                            variant="light"
                            mih={44}
                            leftSection={<IconFingerprint size={18} />}
                            loading={flow.busy && flow.choice === 'passkey'}
                            disabled={flow.busy}
                            onClick={() => void flow.start(null, undefined, 'passkey')}
                        >
                            Entra con una passkey
                        </Button>
                    </>
                ) : null}
            </Stack>
        </form>
    );
};

export default LoginStart;
